import { Color, NgxMatColorPickerComponent } from '@angular-material-components/color-picker';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GeofenceSetting } from '@app-asset-config/models/geofence.model';
import { GeofenceService } from '@app-asset-config/services/geofence.service';
import { dirtyCheck } from '@app-core/models/dirty-check';
import { ConfirmDialogComponent } from '@app-shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { GeofenceListingComponent } from '../geofence-listing/geofence-listing.component';
import { GeofenceHomeComponent } from '../geofence-home/geofence-home.component';

@Component({
  selector: 'app-geofence-setting-create',
  templateUrl: './geofence-setting-create.component.html',
  styleUrls: ['./geofence-setting-create.component.scss'],
})
export class GeofenceSettingCreateComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() setting: GeofenceSetting;
  @Input() default = false;
  @Input() notify = false;

  @Output() create = new EventEmitter();

  @ViewChild('picker') picker!: NgxMatColorPickerComponent;

  public editMode = false;

  public settingForm = new FormGroup({
    propertyName: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(128),
      (control: FormControl) => {
        const value = control.value || '';
        return value.trim().length >= 3 ? null : { minlength: { requiredLength: 3, actualLength: value.trim().length } };
      },
    ]),
    description: new FormControl('', [Validators.maxLength(512)]),
    colourHex: new FormControl(null),
    connected: new FormControl('None'),
    geofenceIds: new FormControl([]),
    geofenceEntry: new FormControl(true),
    geofenceExit: new FormControl(false),
    dvr_driver: new FormControl(false),
    dvr_road: new FormControl(false),
    dms: new FormControl(false),
    adas: new FormControl(true),
    privacy: new FormControl(false),
  });

  public isDirty$ = of(false);
  public isSettingDirty$ = of(false);

  public unSubscribe = new Subject();

  constructor(private geofenceService: GeofenceService, private dialog: MatDialog, private translate: TranslateService) {}

  ngOnInit(): void {
    // checking edit mode
    if (this.setting) {
      if (this.setting.propertyId) {
        this.editMode = true;
        if (this.setting.propertyId === -1) {
          // Default Setting
          this.setting.geofenceIds = [];
        }
      }
      const formValue = this.transformToSettingForm(this.setting);

      this.settingForm.patchValue(formValue);
    }
  }

  ngAfterViewInit(): void {
    const { r, g, b } = this.hexToRgb(this.setting ? this.setting.colourHex : '#D34343');
    this.picker?.select(new Color(r, g, b));

    this.isDirty$ = this.settingForm.valueChanges.pipe(takeUntil(this.unSubscribe), dirtyCheck(of(this.settingForm.value)));

    // create dirty check for default setting - only for device config fields
    const defaultDeviceConfig = this.getDeviceConfigValues(this.geofenceService.geofenceDefaultSettingSubject.value);
    this.isSettingDirty$ = this.settingForm.valueChanges.pipe(
      startWith(this.settingForm.value),
      takeUntil(this.unSubscribe),
      map((formValue) => this.getDeviceConfigValuesFromForm(formValue)), // gets only required field not all
      dirtyCheck(of(defaultDeviceConfig))
    );

    // Duplicate setting
    if (this.setting['clone']) {
      setTimeout(() => {
        this.settingForm.patchValue({ propertyName: this.setting.propertyName + ' copy' });
      }, 150);
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
  }

  // HELPER function
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    // Remove leading hash if present
    hex = hex.replace(/^#/, '');

    // Support short form (#fff) by converting to full form (#ffffff)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }

    // Ensure valid length
    if (hex.length !== 6) {
      console.error('Invalid HEX color.');
      return null;
    }

    // Convert to RGB
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return { r, g, b };
  }

  goBack() {
    this.geofenceService.pop();
  }

  resetConfig() {
    const defaultProperty = this.transformToSettingForm(this.geofenceService.geofenceDefaultSettingSubject.value);
    this.settingForm.patchValue({
      geofenceEntry: defaultProperty.geofenceEntry,
      geofenceExit: defaultProperty.geofenceExit,
      dvr_driver: defaultProperty.dvr_driver,
      dvr_road: defaultProperty.dvr_road,
      dms: defaultProperty.dms,
      adas: defaultProperty.adas,
      privacy: defaultProperty.privacy,
    });
  }

  createSetting() {
    if (this.settingForm.invalid) {
      return;
    }

    // trimming property name
    this.settingForm.get('propertyName').setValue(this.settingForm.value.propertyName.trim(), { emitEvent: false });

    if (this.editMode) {
      return this.updateSetting();
    }

    const body = this.transformFormToAPIBody();

    this.geofenceService.createGeofenceSetting(body).subscribe(
      (res) => {
        if (this.notify) this.create.emit(res);
        this.geofenceService.showToasts(this.translate.instant('geofenceToastSettingCreate'));
        this.geofenceService.fetchGeofenceSetting(true);
        this.goBack();
      },
      (err) => {
        this.geofenceService.showToasts(this.translate.instant('geofenceToastSettingCreateError'), 'error');
        console.log(err);
      }
    );
  }

  updateSetting() {
    const body = this.transformFormToAPIBody();

    this.geofenceService.updateGeofenceSetting(this.setting.propertyId, body).subscribe(
      () => {
        this.geofenceService.showToasts(this.translate.instant('geofenceToastSettingUpdate'));
        this.geofenceService.fetchGeofenceSetting(true);
        this.goBack();
      },
      (err) => {
        this.geofenceService.showToasts(this.translate.instant('geofenceToastSettingUpdateError'), 'error');
        console.log(err);
      }
    );
  }

  // HELPER function
  private transformFormToAPIBody() {
    const { propertyName, description, colourHex, geofenceIds, geofenceEntry, geofenceExit, dvr_driver, dvr_road, dms, adas, privacy } =
      this.settingForm.value;

    const body = {
      propertyName,
      colourHex: '#' + colourHex.hex,
      geofenceAlerts: { geofenceEntry, geofenceExit },
      geofenceRules: [],
    };

    if (description) {
      body['description'] = description;
    }

    if (geofenceIds.length) {
      body['geofenceIds'] = geofenceIds;
    }

    // Video Recording state
    // driver
    body.geofenceRules.push({
      target: 'DVR',
      action: dvr_driver ? 'ENABLE' : 'DISABLE',
      appliesTo: ['DRIVER'],
    });
    // road
    body.geofenceRules.push({
      target: 'DVR',
      action: dvr_road ? 'ENABLE' : 'DISABLE',
      appliesTo: ['ROAD'],
    });

    // AI telematics state
    body.geofenceRules.push({
      target: 'DMS',
      action: dms ? 'ENABLE' : 'DISABLE',
    });

    body.geofenceRules.push({
      target: 'ADAS',
      action: adas ? 'ENABLE' : 'DISABLE',
    });

    // privacy
    body.geofenceRules.push({
      target: 'PRIVACY',
      action: privacy ? 'ENABLE' : 'DISABLE',
    });

    return body;
  }

  // HELPER function
  private transformToSettingForm(setting: GeofenceSetting) {
    const { propertyName, description, colourHex, geofenceIds, geofenceAlerts, geofenceRules } = setting;

    const formValue = {
      propertyName,
      description,
      colourHex: { hex: colourHex.replace('#', '') },
      geofenceIds,
      connected: geofenceIds.length
        ? `${geofenceIds.length} ${this.translate.instant('geofenceConnected')}`
        : this.translate.instant('geofenceNone'),
      geofenceEntry: geofenceAlerts.geofenceEntry,
      geofenceExit: geofenceAlerts.geofenceExit,
      dvr_driver: false,
      dvr_road: false,
      dms: false,
      adas: false,
      privacy: false,
    };

    // setting rules on the form
    geofenceRules.forEach((obj) => {
      // setting form value for dvr
      // For DISABLE action already initialized the formValue with false
      if (obj.target === 'DVR' && obj.action === 'ENABLE') {
        obj.appliesTo.forEach((value) => {
          if (value === 'DRIVER') formValue.dvr_driver = true;
          if (value === 'ROAD') formValue.dvr_road = true;
        });
      }

      // setting form value for dms
      if (obj.target === 'DMS') {
        formValue.dms = obj.action === 'ENABLE';
      }

      // setting form value for adas
      if (obj.target === 'ADAS') {
        formValue.adas = obj.action === 'ENABLE';
      }

      // setting form value for privacy
      if (obj.target === 'PRIVACY') {
        formValue.privacy = obj.action === 'ENABLE';
      }
    });

    return formValue;
  }

  // HELPER function to get only device config values for comparison
  private getDeviceConfigValues(setting: GeofenceSetting) {
    const formValue = this.transformToSettingForm(setting);
    return {
      geofenceEntry: formValue.geofenceEntry,
      geofenceExit: formValue.geofenceExit,
      dvr_driver: formValue.dvr_driver,
      dvr_road: formValue.dvr_road,
      dms: formValue.dms,
      adas: formValue.adas,
      privacy: formValue.privacy,
    };
  }

  // HELPER function to get device config values from form value
  private getDeviceConfigValuesFromForm(formValue: any) {
    return {
      geofenceEntry: formValue.geofenceEntry,
      geofenceExit: formValue.geofenceExit,
      dvr_driver: formValue.dvr_driver,
      dvr_road: formValue.dvr_road,
      dms: formValue.dms,
      adas: formValue.adas,
      privacy: formValue.privacy,
    };
  }

  duplicateSetting() {
    this.goBack();

    this.geofenceService.navigateTo(GeofenceSettingCreateComponent, {
      setting: {
        ...this.setting,
        propertyId: undefined,
        propertyName: this.setting.propertyName,
        geofenceIds: [],
        clone: true,
      },
    });
  }

  deleteSetting() {
    if (!this.setting.geofenceIds.length) {
      this.geofenceService.deleteGeofenceSetting(this.setting.propertyId).subscribe(
        () => {
          this.geofenceService.showToasts(this.translate.instant('geofenceToastSettingDelete'));
          this.geofenceService.fetchGeofenceSetting(true);
          this.goBack();
        },
        (err) => {
          this.geofenceService.showToasts(this.translate.instant('geofenceToastSettingDeleteError'), 'error');
          console.log(err);
        }
      );
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      autoFocus: false,
      disableClose: true,
      panelClass: ['mobile-modal'],
      data: {
        title: this.translate.instant('geofenceModalDeleteGeofenceSettingHeading'),
        content: this.translate.instant('geofenceModalDeleteGeofenceSettingContent'),
        actions: [
          { actionButton: this.translate.instant('geofenceModalDisableActionCancel'), dialogCloseValue: false },
          { actionButton: this.translate.instant('geofenceModalDeleteSettingActionManage'), dialogCloseValue: true, primary: true },
        ],
        showCloseIcon: true,
      },
    });

    dialogRef.afterClosed().subscribe((value: any) => {
      if (value) {
        this.geofenceService.navigateTo(GeofenceHomeComponent);
      }
    });
  }

  openGeofenceSettingSelection() {
    this.geofenceService.navigateTo(GeofenceListingComponent, {
      mode: 'multiselect',
      from: 'setting',
      preSelect: this.setting.geofenceIds,
      'output:selected': this.applySettingToSelectedGeofences.bind(this),
    });
  }

  // function will be used only in edit mode
  // cause selected geofence UI appears only in edit mode
  applySettingToSelectedGeofences(geofenceIds: number[]) {
    if (geofenceIds.length === 0) return;

    const promises = [];

    geofenceIds.forEach((id) => {
      promises.push(this.geofenceService.updateGeofence(id, { propertyId: this.setting.propertyId }).toPromise());
    });

    Promise.all(promises)
      .then(() => {
        this.geofenceService.showToasts(this.translate.instant('geofenceToastGeofenceUpdated'));
        this.geofenceService.fetchGeofence(true);
        this.resetGeofenceIds();

        this.setting.geofenceIds = geofenceIds;

        const form = this.transformToSettingForm(this.setting);
        this.settingForm.patchValue(form);
      })
      .catch((err) => {
        console.log(err);
        this.geofenceService.showToasts(this.translate.instant('geofenceToastGeofenceUpdationError'), 'error');
      });
  }

  // used only when user has selected geofence, and updated the setting
  private resetGeofenceIds() {
    // fetch setting by settingId
    this.geofenceService.listGeofenceSetting({ skip: 0, limit: 1, propertyId: this.setting.propertyId }).subscribe(
      (res: any) => {
        if (res.rows?.length) {
          const form = this.transformToSettingForm(res.rows[0]);
          this.settingForm.patchValue({
            geofenceIds: form.geofenceIds,
            connected: form.connected,
          });
        }
      },
      (err) => {
        this.geofenceService.showToasts('Fetching updated setting failed');
        console.log(err);
      }
    );
  }
}
